import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject, Subject, forkJoin } from 'rxjs';
import { map, catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { LookupService } from './lookup.service';

@Injectable({
    providedIn: 'root',
})
export class PhotoService {
    // Profile picture caches (existing)
    private photoBlobCache = new Map<string, Blob>();
    private photoUrlCache = new Map<string, string>();
    private photoUrlPending = new Map<string, Observable<string>>();

    // Activity photo/video caches (separate)
    private activityBlobCache = new Map<string, Blob>();
    private activityUrlCache = new Map<string, string>();
    private activityUrlPending = new Map<string, Observable<string>>();

    // Track object URLs for cleanup
    private objectUrlsToRevoke = new Set<string>();

    // Default photo URL
    private readonly DEFAULT_PHOTO = 'assets/layout/images/male.png';

    // Lenient UUID validation regex (supports with/without hyphens)
    private readonly UUID_REGEX = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

    // Cleanup subject for component destruction
    private destroy$ = new Subject<void>();

    constructor(private lookupService: LookupService) {
        // Clear temporary caches but preserve blob cache for performance
        this.photoUrlCache.clear();
        this.photoUrlPending.clear();
        this.objectUrlsToRevoke.clear();
        // Note: photoBlobCache is NOT cleared to maintain persistence across app sessions
    }

    /**
     * Get photo URL for a single photo UID with caching
     * @param photoUid The photo UID to load
     * @returns Observable of the photo URL
     */
    /**
     * Validate if a string is a valid UUID
     * @param uuid The string to validate
     * @returns True if valid UUID
     */
    public isValidUUID(uuid: string): boolean {
        return this.UUID_REGEX.test(uuid);
    }

    getPhotoUrl(photoUid: string): Observable<string> {
        if (!photoUid || photoUid === this.DEFAULT_PHOTO || !this.isValidUUID(photoUid)) {
            return of(this.DEFAULT_PHOTO);
        }

        // Check if we have the blob cached
        const cachedBlob = this.photoBlobCache.get(photoUid);
        if (cachedBlob) {
            const url = this.createObjectUrl(cachedBlob);
            return of(url);
        }

        // Check if request is already pending
        const pending = this.photoUrlPending.get(photoUid);
        if (pending) {
            return pending;
        }

        // Create new request
        const request$ = this.lookupService
            .queryDocumentByPhotoUid(photoUid)
            .pipe(
                map((response: HttpResponse<Blob>) => {
                    const body = response?.body;
                    if (!body) {
                        return this.DEFAULT_PHOTO;
                    }

                    // Cache the blob
                    this.photoBlobCache.set(photoUid, body);

                    // Create object URL
                    const url = this.createObjectUrl(body);
                    this.photoUrlCache.set(photoUid, url);

                    return url;
                }),
                catchError((error) => {
                    return of(this.DEFAULT_PHOTO);
                }),
                finalize(() => {
                    this.photoUrlPending.delete(photoUid);
                }),
                shareReplay(1)
            );

        this.photoUrlPending.set(photoUid, request$);
        return request$;
    }


    /**
     * Create object URL from blob and track it for cleanup
     * @param blob The blob to create URL for
     * @returns The object URL
     */
    private createObjectUrl(blob: Blob): string {
        const url = URL.createObjectURL(blob);
        this.objectUrlsToRevoke.add(url);
        return url;
    }

    /**
     * Get cached photo URL if available, recreating from blob if necessary
     * @param photoUid The photo UID
     * @returns The cached URL or undefined
     */
    getCachedPhotoUrl(photoUid: string): string | undefined {
        // First check if we have a cached URL
        let cachedUrl = this.photoUrlCache.get(photoUid);
        if (cachedUrl) {
            return cachedUrl;
        }

        // If no URL but we have the blob cached, recreate the object URL
        const cachedBlob = this.photoBlobCache.get(photoUid);
        if (cachedBlob) {
            cachedUrl = this.createObjectUrl(cachedBlob);
            this.photoUrlCache.set(photoUid, cachedUrl);
            return cachedUrl;
        }

        return undefined;
    }

    /**
     * Check if photo is cached
     * @param photoUid The photo UID
     * @returns True if cached
     */
    isPhotoCached(photoUid: string): boolean {
        const isCached = this.photoBlobCache.has(photoUid);
        return isCached;
    }

    /**
     * Preload photos for better performance
     * @param photoUids Array of photo UIDs to preload
     * @returns Observable that completes when all photos are loaded
     */
    preloadPhotos(photoUids: string[]): Observable<void> {
        if (!photoUids || photoUids.length === 0) {
            return of(undefined);
        }

        // Filter out already cached photos and invalid UIDs
        const photosToLoad = photoUids.filter(photoUid =>
            photoUid &&
            photoUid.trim() &&
            photoUid !== this.DEFAULT_PHOTO &&
            this.isValidUUID(photoUid) &&
            !this.isPhotoCached(photoUid)
        );

        if (photosToLoad.length === 0) {
            return of(undefined);
        }

        // Create observables for each photo load
        const photoLoadObservables = photosToLoad.map(photoUid =>
            this.getPhotoUrl(photoUid)
        );

        // Return observable that completes when all photos are loaded
        return forkJoin(photoLoadObservables).pipe(
            map(() => {
                return undefined;
            }), // Convert to void
            catchError((error) => {
                return of(undefined);
            }) // Don't fail if some photos fail to load
        );
    }

    /**
     * Cleanup specific photo data
     * @param photoUid The photo UID to cleanup
     */
    cleanupPhoto(photoUid: string): void {
        const cachedUrl = this.photoUrlCache.get(photoUid);
        if (cachedUrl) {
            this.revokeObjectUrl(cachedUrl);
            this.objectUrlsToRevoke.delete(cachedUrl);
        }
        this.photoBlobCache.delete(photoUid);
        this.photoUrlCache.delete(photoUid);
        this.photoUrlPending.delete(photoUid);
    }

    /**
     * Cleanup object URL
     * @param url The URL to revoke
     */
    private revokeObjectUrl(url: string): void {
        try {
            URL.revokeObjectURL(url);
        } catch (error) {
            console.warn('Failed to revoke object URL:', error);
        }
    }

    /**
     * Clear all cached photo data (profile pictures)
     */
    clearCache(): void {
        // Revoke all object URLs
        this.photoUrlCache.forEach(url => {
            this.revokeObjectUrl(url);
        });

        // Clear all profile picture caches
        this.photoBlobCache.clear();
        this.photoUrlCache.clear();
        this.photoUrlPending.clear();
        this.objectUrlsToRevoke.clear();
    }

    /**
     * Clear all cached data (both profile pictures and activity photos)
     */
    clearAllCache(): void {
        // Clear profile picture cache
        this.clearCache();

        // Clear activity photo cache
        this.clearActivityPhotoCache();
    }

    /**
     * Cleanup method for component destruction - only revokes object URLs
     * Keeps blob cache intact for app-wide persistence
     */
    cleanup(): void {
        // Only revoke object URLs, keep blob cache for performance
        this.photoUrlCache.forEach(url => {
            this.revokeObjectUrl(url);
        });
        this.activityUrlCache.forEach(url => {
            this.revokeObjectUrl(url);
        });

        // Clear URL caches but keep blob cache
        this.photoUrlCache.clear();
        this.photoUrlPending.clear();
        this.activityUrlCache.clear();
        this.activityUrlPending.clear();
        this.objectUrlsToRevoke.clear();
    }

    /**
     * Get activity photo URL with caching (separate from profile pictures)
     * @param photoUid The activity photo UID to load
     * @returns Observable of the photo URL
     */
    getActivityPhotoUrl(photoUid: string): Observable<string> {
        if (!photoUid || photoUid.trim() === '') {
            return of('');
        }

        // Check if we have the blob cached
        const cachedBlob = this.activityBlobCache.get(photoUid);
        if (cachedBlob) {
            const url = this.createObjectUrl(cachedBlob);
            return of(url);
        }

        // Check if request is already pending
        const pending = this.activityUrlPending.get(photoUid);
        if (pending) {
            return pending;
        }

        // Create new request
        const request$ = this.lookupService
            .queryDocumentByPhotoUid(photoUid)
            .pipe(
                map((response: HttpResponse<Blob>) => {
                    const body = response?.body;
                    if (!body) return '';

                    // Cache the blob
                    this.activityBlobCache.set(photoUid, body);

                    // Create object URL
                    const url = this.createObjectUrl(body);
                    this.activityUrlCache.set(photoUid, url);

                    return url;
                }),
                catchError((error) => {
                    return of('');
                }),
                finalize(() => {
                    this.activityUrlPending.delete(photoUid);
                }),
                shareReplay(1)
            );

        this.activityUrlPending.set(photoUid, request$);
        return request$;
    }

    /**
     * Get cached activity photo URL if available (separate from profile pictures)
     * @param photoUid The activity photo UID
     * @returns The cached URL or undefined
     */
    getCachedActivityPhotoUrl(photoUid: string): string | undefined {
        // First check if we have a cached URL
        let cachedUrl = this.activityUrlCache.get(photoUid);
        if (cachedUrl) {
            return cachedUrl;
        }

        // If no URL but we have the blob cached, recreate the object URL
        const cachedBlob = this.activityBlobCache.get(photoUid);
        if (cachedBlob) {
            cachedUrl = this.createObjectUrl(cachedBlob);
            this.activityUrlCache.set(photoUid, cachedUrl);
            return cachedUrl;
        }

        return undefined;
    }

    /**
     * Check if activity photo is cached (separate from profile pictures)
     * @param photoUid The activity photo UID
     * @returns True if cached
     */
    isActivityPhotoCached(photoUid: string): boolean {
        return this.activityBlobCache.has(photoUid);
    }

    /**
     * Preload activity photos for better performance (separate from profile pictures)
     * @param photoUids Array of activity photo UIDs to preload
     * @returns Observable that completes when all photos are loaded
     */
    preloadActivityPhotos(photoUids: string[]): Observable<void> {
        if (!photoUids || photoUids.length === 0) {
            return of(undefined);
        }

        // Filter out already cached photos and invalid UIDs
        const photosToLoad = photoUids.filter(photoUid =>
            photoUid &&
            photoUid.trim() &&
            !this.isActivityPhotoCached(photoUid)
        );

        if (photosToLoad.length === 0) {
            return of(undefined);
        }

        // Create observables for each photo load
        const photoLoadObservables = photosToLoad.map(photoUid =>
            this.getActivityPhotoUrl(photoUid)
        );

        // Return observable that completes when all photos are loaded
        return forkJoin(photoLoadObservables).pipe(
            map(() => undefined), // Convert to void
            catchError((error) => {
                return of(undefined);
            }) // Don't fail if some photos fail to load
        );
    }

    /**
     * Download activity photo in background and notify when complete (for edit component)
     * @param photoUid The activity photo UID to download
     * @returns Observable that emits the URL when download completes
     */
    downloadActivityPhotoInBackground(photoUid: string): Observable<string> {
        if (!photoUid || photoUid.trim() === '') {
            return of('');
        }

        // If already cached, return immediately
        const cachedUrl = this.getCachedActivityPhotoUrl(photoUid);
        if (cachedUrl) {
            return of(cachedUrl);
        }

        // Start background download
        return this.getActivityPhotoUrl(photoUid);
    }

    /**
     * Cleanup specific activity photo data (separate from profile pictures)
     * @param photoUid The activity photo UID to cleanup
     */
    cleanupActivityPhoto(photoUid: string): void {
        const cachedUrl = this.activityUrlCache.get(photoUid);
        if (cachedUrl) {
            this.revokeObjectUrl(cachedUrl);
            this.objectUrlsToRevoke.delete(cachedUrl);
        }
        this.activityBlobCache.delete(photoUid);
        this.activityUrlCache.delete(photoUid);
        this.activityUrlPending.delete(photoUid);
    }

    /**
     * Clear all cached activity photo data (separate from profile pictures)
     */
    clearActivityPhotoCache(): void {
        // Revoke all object URLs
        this.activityUrlCache.forEach(url => {
            this.revokeObjectUrl(url);
        });

        // Clear all activity caches
        this.activityBlobCache.clear();
        this.activityUrlCache.clear();
        this.activityUrlPending.clear();
    }

    /**
     * Get default photo URL
     */
    getDefaultPhoto(): string {
        return this.DEFAULT_PHOTO;
    }
}
